import React from "react";
import styled from "styled-components";
import {
  faGithub,
  faSlack,
  faTwitter,
} from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Grid } from "@components";
import { useHasFeature } from "@hooks";
import { TWITTER_URL } from "@utils";

import { MailingListBox } from "./MailingListBox";

const Container = styled.footer`
  background: var(--indigo-gradient-245);
  &,
  & a,
  & input {
    color: white;
  }

  &,
  & h1,
  & h2,
  & h3,
  & h4,
  & h5,
  & h6 {
    font-weight: var(--global-font-weight-regular);
  }

  border-top: 12px solid var(--global-green);
`;

const FooterGrid = styled(Grid)`
  margin: auto;
  max-width: 1160px;
  padding: 80px 20px 68px;
  text-align: left;
`;

const Panel = styled.section`
  padding-left: 20px;
  border-left: 1px solid #f2f8ff;
`;

const LinksPanel = styled(Panel)`
  display: flex;
  justify-content: space-between;
`;

const SocialLinks = styled.section`
  margin-right: 12px;
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  & + & {
    margin-top: 28px;
  }
`;

const LinkList = styled.ul`
  margin: 0;
  padding-left: 0;
  list-style-type: none;
`;

const LinkListItem = styled.li`
  font-size: var(--global-font-size-s);
  line-height: 36px;
  text-decoration: none;
`;

const LinkListHiddenItem = styled.li`
  display: none;
`;

const LinkHeading = styled.h4`
  margin: 0;
  font-size: var(--global-font-size-s);
  line-height: 36px;
  color: #c6c8cc;
`;

export const Footer: React.FC<{}> = () => {
  const hasNonStandardTOC = useHasFeature("hasNonStandardTOC");
  return (
    <Container>
      <FooterGrid minWidth={340}>
        <LinksPanel>
          <SocialLinks>
            <SocialLink href="https://r2c.dev/slack">
              <FontAwesomeIcon icon={faSlack} size="2x" fixedWidth />
              <span>Slack</span>
            </SocialLink>
            <SocialLink href="https://github.com/returntocorp/semgrep">
              <FontAwesomeIcon icon={faGithub} size="2x" fixedWidth />
              <span>GitHub</span>
            </SocialLink>
            <SocialLink href={TWITTER_URL}>
              <FontAwesomeIcon icon={faTwitter} size="2x" fixedWidth />
              <span>Twitter</span>
            </SocialLink>
          </SocialLinks>
          <Grid columns={3}>
            <LinkList>
              <LinkHeading>Learn</LinkHeading>
              <LinkListItem>
                <a href="/docs/">Docs</a>
              </LinkListItem>
              <LinkListItem>
                <a href="/docs/writing-rules/rule-ideas/">Examples</a>
              </LinkListItem>
              <LinkListItem>
                <a href="/learn">Tour</a>
              </LinkListItem>
            </LinkList>
            <LinkList>
              <LinkHeading>Product</LinkHeading>
              <LinkListItem>
                <a href="/privacy">Privacy</a>
              </LinkListItem>
              <LinkListItem>
                <a href="https://github.com/returntocorp/semgrep/issues">
                  Issues
                </a>
              </LinkListItem>
              {!hasNonStandardTOC && (
                <LinkListItem>
                  <a href="/terms">Terms of service</a>
                </LinkListItem>
              )}
            </LinkList>
            <LinkList>
              <LinkHeading>About</LinkHeading>
              <LinkListItem>
                <a href="https://r2c.dev/blog">Blog</a>
              </LinkListItem>
              <LinkListItem>
                <a href="https://r2c.dev/team">About us</a>
              </LinkListItem>
              <LinkListItem>
                <a href="/contact-us">Contact us</a>
              </LinkListItem>
              <LinkListHiddenItem>
                <a href="https://semgrep.dev/api/supersecret">Secret</a>
              </LinkListHiddenItem>
            </LinkList>
          </Grid>
        </LinksPanel>
        <Panel>
          <p>Get release updates by signing up for our newsletter:</p>
          <MailingListBox />
          <p>
            &copy; {new Date().getFullYear()} <a href="https://r2c.dev">r2c</a>.
            Semgrep is a registered trademark of r2c.
          </p>
        </Panel>
      </FooterGrid>
    </Container>
  );
};
